import { useState, useEffect, useCallback } from 'react';
import { Settings, Users, Building2, Database, Save, RefreshCw } from 'lucide-react';
import type { TenantSubscription } from '@yawi/types';
import { Card, Button } from '@/core/ui/components';
import { useServices, useToast } from '@/app/providers';

interface TenantQuotasProps {
  tenantId: string;
}

const GB = 1024 * 1024 * 1024;

interface Limits {
  users: number;
  units: number;
  storageGb: number;
}

/**
 * Límites efectivos = `custom_*_limit` de la suscripción o, si no hay, los
 * incluidos en el plan (misma regla que UsageTrackingService en el backend).
 * Los bigint llegan como string desde MySQL, de ahí los Number().
 */
function effectiveLimits(sub: TenantSubscription): Limits {
  return {
    users: Number(sub.customUsersLimit || sub.plan?.includedUsers || 0),
    units: Number(sub.customUnitsLimit || sub.plan?.includedUnits || 0),
    storageGb: Number(sub.customStorageLimit || sub.plan?.includedStorageBytes || 0) / GB,
  };
}

export function TenantQuotas({ tenantId }: TenantQuotasProps) {
  const { billingRepository } = useServices();
  const { showToast } = useToast();

  const [subscription, setSubscription] = useState<TenantSubscription | null>(null);
  const [edited, setEdited] = useState<Limits | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuotas = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const sub = await billingRepository.getTenantSubscription(tenantId);
      setSubscription(sub);
      setEdited(sub ? effectiveLimits(sub) : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los límites');
    } finally {
      setIsLoading(false);
    }
  }, [billingRepository, tenantId]);

  useEffect(() => {
    fetchQuotas();
  }, [fetchQuotas]);

  const current = subscription ? effectiveLimits(subscription) : null;
  const hasChanges =
    !!current &&
    !!edited &&
    (current.users !== edited.users ||
      current.units !== edited.units ||
      current.storageGb !== edited.storageGb);

  const handleSave = async () => {
    if (!edited || !current) return;
    setIsSaving(true);
    try {
      // Solo los campos tocados: los demás siguen heredando del plan
      await billingRepository.updateSubscription(tenantId, {
        ...(edited.users !== current.users && { customUsersLimit: edited.users }),
        ...(edited.units !== current.units && { customUnitsLimit: edited.units }),
        ...(edited.storageGb !== current.storageGb && {
          customStorageLimit: Math.round(edited.storageGb * GB),
        }),
      });
      showToast('success', 'Límites actualizados');
      // Relectura para mostrar lo que quedó guardado
      await fetchQuotas();
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'No se pudieron guardar los límites');
    } finally {
      setIsSaving(false);
    }
  };

  const fields: { key: keyof Limits; label: string; icon: typeof Users; step: number }[] = [
    { key: 'users', label: 'Usuarios', icon: Users, step: 1 },
    { key: 'units', label: 'Unidades', icon: Building2, step: 1 },
    { key: 'storageGb', label: 'Almacenamiento (GB)', icon: Database, step: 0.5 },
  ];

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Límites de la suscripción
        </h3>
        {subscription && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchQuotas}
              leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
            >
              Restablecer
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              isLoading={isSaving}
              disabled={!hasChanges}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Guardar
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
        </div>
      ) : error ? (
        <div className="text-center py-6">
          <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          <Button className="mt-3" variant="outline" size="sm" onClick={fetchQuotas}>
            Reintentar
          </Button>
        </div>
      ) : !subscription || !edited ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
          El tenant no tiene suscripción. Los límites se definen al crearla en la pestaña Billing.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {fields.map(({ key, label, icon: Icon, step }) => (
              <div
                key={key}
                className="flex items-center gap-3 p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50"
              >
                <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1">
                  <label htmlFor={`quota-${key}`} className="text-sm text-gray-500 dark:text-gray-400">
                    {label}
                  </label>
                  <input
                    id={`quota-${key}`}
                    type="number"
                    min={0}
                    step={step}
                    value={edited[key]}
                    onChange={(e) =>
                      setEdited((prev) => (prev ? { ...prev, [key]: Number(e.target.value) || 0 } : prev))
                    }
                    className="w-full mt-1 px-2 py-1 text-sm font-medium bg-white dark:bg-slate-900 border border-gray-200 dark:border-gray-700 rounded"
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
            Sin valor propio se usan los incluidos en el plan {subscription.plan?.name ?? ''}. Billing usa
            estos límites para calcular excedentes.
          </p>
        </>
      )}
    </Card>
  );
}
